'use client';

import { Theme, useTheme } from '@emotion/react';
import { DateTime } from 'luxon';
import RulesOfflineSVG from '../../../public/img/rules-offline.svg';
import RulesOfficeSVG from '../../../public/img/rules-office.svg';
import RulesOnlineSVG from '../../../public/img/rules-online.svg';
import { Container } from '../../../components/layout/Container';
import { H1, H2, H3 } from '../../../components/styled/typography';
import { Link } from '../../../components/styled/Link';
import { DEVICE_SCREENS_MAX_STRING } from '../../../lib/consts';
import { BreadCrumbs } from '../../../components/blocks/BreadCrumbs';
import logoImg from '../../../public/img/logo.png';
import { City } from '../../../models';
import { Page as PageModel } from '../../../models/page';

type IRulesItemProps = {
  number: string;
  background: string;
  title: string;
  description: string;
  theme: Theme;
};

function RulesItem({
  theme,
  number,
  background,
  title,
  description,
}: IRulesItemProps) {
  return (
    <div
      css={{
        position: 'relative',
        background,
        padding: '20px 30px',
        minHeight: 170,
        [DEVICE_SCREENS_MAX_STRING.md]: {
          minHeight: 156,
        },
      }}
    >
      <div
        css={{
          position: 'absolute',
          top: -50,
          left: 30,
          fontSize: 60,
          fontWeight: 700,
          color: theme.colors.red,
        }}
      >
        {number}
      </div>
      <div
        css={{
          fontWeight: 700,
          fontSize: 16,
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            fontSize: 14,
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            fontSize: 16,
          },
        }}
      >
        {title}
      </div>
      <p
        css={{
          marginTop: 28,
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            fontSize: 11,
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            fontSize: 16,
            marginTop: 0,
          },
        }}
      >
        {description}
      </p>
    </div>
  );
}

type IPageComponentProps = {
  currentCity: City;
  page: PageModel;
  pagesRulesList: {
    results: PageModel[];
  };
};

export function PageComponent({
  currentCity,
  page,
  pagesRulesList,
}: IPageComponentProps) {
  const theme = useTheme();

  const getAge = () => {
    const birth = DateTime.fromISO('2015-09-01');
    const now = DateTime.now();
    return Math.floor(now.diff(birth, 'years').years);
  };

  return (
    <Container
      css={{
        padding: '32px 14px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '32px 16px',
        },
      }}
    >
      <BreadCrumbs currentCity={currentCity} items={[{ title: page.title }]} />
      <H1
        css={{
          fontFamily: 'Montserrat',
          color: '#333333',
          fontWeight: 700,
          marginTop: 24,
          [DEVICE_SCREENS_MAX_STRING.md]: {
            marginTop: 12,
          },
        }}
      >
        Пользоваться сервисом выгодно!
      </H1>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <div
          css={{
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.43,
            padding: '16px 0',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              padding: '24px 0 20px 0',
            },
          }}
        >
          <p>
            Предложения доступны у нас со скидкой до 90% или без сервисного
            сбора.
          </p>
          <p>
            Наш сервис надёжно работает больше {getAge()} лет и каждый месяц
            помогает экономить деньги на развлечениях 30 000+ человек. За это мы
            получили более 10 000 положительных отзывов, с которыми вы можете
            ознакомиться в социальных сетях проекта.
          </p>
          <p>
            Наша цель - сделать досуговую культуру для горожан и гостей города
            интереснее, разнообразнее и выгоднее, а сам выбор проще, быстрее и
            удобнее!
          </p>
        </div>
        <H3 css={{ fontWeight: 700, fontSize: 20, marginTop: 20 }}>
          Как пользоваться сервисом? У нас используется 2 системы:
        </H3>
        <H2 css={{ marginTop: 20, fontSize: 32 }}>Покупка онлайн</H2>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginTop: 60,
            },
          }}
        >
          <div
            css={{
              position: 'relative',
              zIndex: 0,
              '::before': {
                position: 'absolute',
                left: 100,
                top: 35,
                content: `url(${logoImg.src})`,
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                '::before': {
                  left: 70,
                  top: 20,
                  transform: 'scale(0.7)',
                },
              },
            }}
          >
            <RulesOnlineSVG
              css={{
                width: 115,
                height: 115,
                zIndex: 1,
                position: 'relative',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  width: 95,
                  height: 95,
                },
              }}
            />
          </div>
          <div
            css={{
              fontWeight: 700,
              fontSize: 24,
              marginTop: 34,
              [DEVICE_SCREENS_MAX_STRING.lg]: {
                fontSize: 18,
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                fontSize: 16,
              },
            }}
          >
            При оплате онлайн через наш сервис
          </div>
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 16,
            marginLeft: 115,
            marginTop: 40,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginLeft: 0,
              gridTemplateColumns: '1fr',
              gap: 40,
            },
          }}
        >
          <RulesItem
            background='#fff'
            number='1'
            title='Выберите место или категорию билетов'
            description='Ура! Билеты практически у вас в кармане, осталось только оплатить их'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='2'
            title='Заполните форму покупки и оплатите билеты'
            description='Внимательно заполните свои контактные данные и оплатите билеты онлайн с помощью банковской карты'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='3'
            title='Приходите на мероприятие'
            description='Учтите, что для некоторых мероприятий нужен распечатанный электронный билет'
            theme={theme}
          />
        </div>
        <H2 css={{ marginTop: 20, fontSize: 32 }}>Бесплатное бронирование</H2>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginTop: 60,
            },
          }}
        >
          <div>
            <RulesOfflineSVG
              css={{
                width: 115,
                height: 115,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  width: 95,
                  height: 95,
                },
              }}
            />
          </div>
          <div
            css={{
              fontWeight: 700,
              fontSize: 24,
              [DEVICE_SCREENS_MAX_STRING.lg]: {
                fontSize: 18,
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                fontSize: 16,
              },
            }}
          >
            При оплате на месте
          </div>
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 16,
            marginLeft: 115,
            marginTop: 40,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginLeft: 0,
              gridTemplateColumns: '1fr',
              gap: 40,
            },
          }}
        >
          <RulesItem
            background='#fff'
            number='1'
            title='Заполните форму бронирования'
            description='Проверьте электронную почту, в письме Вы найдёте всю необходимую информацию'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='2'
            title='Выкупите билеты за наличные'
            description='На месте сбора группы/начала мероприятия назовите организатору данные, указанные при бронировании, и оплатите билеты'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='3'
            title='Хорошо проведите время'
            description='Ловите завистливые взгляды тех, кто не знает про Горбилет :)'
            theme={theme}
          />
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginTop: 60,
            },
          }}
        >
          <div>
            <RulesOfficeSVG
              css={{
                width: 115,
                height: 115,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  width: 95,
                  height: 95,
                },
              }}
            />
          </div>
          <div
            css={{
              fontWeight: 700,
              fontSize: 24,
              marginTop: 34,
              [DEVICE_SCREENS_MAX_STRING.lg]: {
                fontSize: 18,
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                fontSize: 16,
              },
            }}
          >
            При оплате в офисе турфирмы
          </div>
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 16,
            marginLeft: 115,
            marginTop: 40,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginLeft: 0,
              gridTemplateColumns: '1fr',
              gap: 40,
            },
          }}
        >
          <RulesItem
            background='#fff'
            number='1'
            title='Заполните форму бронирования'
            description='Проверьте электронную почту: Вы получите письмо-подтверждение бронирования от сервиса Горбилет. Организатор также получит письмо с данными о Вашем бронировании. Вам остается только дождаться звонка менеджера турфирмы и уточнить у него все детали.'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='2'
            title='Следуйте инструкциям'
            description='Сотрудник организатора подскажет Вам, как оплатить билеты. У него можно уточнить и адрес турфирмы, если потребуется'
            theme={theme}
          />
          <RulesItem
            background='#fff'
            number='3'
            title='Получайте удовольствие'
            description='И приходите ещё :)'
            theme={theme}
          />
        </div>
        {pagesRulesList.results.length > 0 && (
          <div
            css={{
              marginTop: 120,
              display: 'flex',
              justifyContent: 'center',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                flexDirection: 'column',
                alignItems: 'center',
              },
            }}
          >
            <div
              css={{
                marginRight: 20,
                fontWeight: 700,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  display: 'none',
                },
              }}
            >
              Документы:
            </div>
            <ul
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 20,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  flexDirection: 'column',
                  gap: 0,
                },
              }}
            >
              <li
                css={{
                  fontWeight: 700,
                  listStyleType: 'none',
                  display: 'none',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'block',
                  },
                }}
              >
                Документы:
              </li>
              {pagesRulesList.results.map((item) => (
                <li key={item.slug}>
                  <Link
                    href={['', 'pages', item.slug, ''].join('/')}
                    css={{ ':hover': { color: 'inherit' } }}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </Container>
  );
}
